import { FC, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeMode } from '@/hooks/useThemeMode';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import TurboWordmark from './TurboWordmark';
import { useNavigation } from '@/context/NavigationContext';
import { isOverlayRoute, stripHashAndQuery } from '@/lib/navigationRoutes';

interface FloatingNavProps {
  onMenuClick?: () => void;
  forceVisible?: boolean;
}

type FloatingNavItem = {
  label: string;
  href: string;
  overlay?: string;
};

const NAV_ITEMS: FloatingNavItem[] = [
  { label: 'About', href: '/about', overlay: '/about' },
  { label: 'Rapid SaaS', href: '/services', overlay: '/services' },
  { label: 'AI Agents', href: '/services/nexus', overlay: '/services/nexus' },
  { label: 'Work', href: '/work', overlay: '/work' },
  { label: 'Resources', href: '/blog', overlay: '/blog' },
  { label: 'Contact', href: '/contact', overlay: '/contact' },
];

type NavigationState = {
  backgroundLocation?: Location;
};

const FloatingNav: FC<FloatingNavProps> = ({ onMenuClick, forceVisible = false }) => {
  const [isVisible, setIsVisible] = useState(forceVisible);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isDark, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as NavigationState | undefined) ?? undefined;
  const backgroundLocation = (state?.backgroundLocation as Location | undefined) ?? location;
  const { setMenuOpen } = useNavigation();
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    if (forceVisible) {
      setIsVisible(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const lastScrollY = lastScrollYRef.current;

      if (currentScrollY > 100) {
        if (currentScrollY < lastScrollY) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      } else {
        setIsVisible(false);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [forceVisible]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavigation = (href: string, overlay?: string) => {
    if (href === '/contact') {
      if (typeof window !== 'undefined') {
        // @ts-expect-error custom drawer event dispatched globally
        window.dispatchEvent(new Event('open-contact'));
      } else {
        navigate('/contact');
      }
      setMenuOpen(false);
      setMobileMenuOpen(false);
      return;
    }

    const target = overlay || href;
    const targetPath = stripHashAndQuery(target);

    if (isOverlayRoute(targetPath)) {
      navigate(target, {
        state: { backgroundLocation },
      });
    } else {
      navigate(href);
    }
    setMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const currentLabel = (() => {
    const currentPath = stripHashAndQuery(backgroundLocation.pathname ?? '');
    const match = NAV_ITEMS.find((item) => stripHashAndQuery(item.href) === currentPath);
    return match?.label ?? 'Menu';
  })();

  const handleMenuClick = () => {
    const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : true;

    if (isMobile) {
      setMobileMenuOpen((prev) => !prev);
      return;
    }

    if (onMenuClick) {
      onMenuClick();
      return;
    }

    setMenuOpen(true);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={containerRef}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ 
            type: 'spring', 
            stiffness: 260, 
            damping: 20 
          }}
          className="fixed top-4 left-0 right-0 z-[220] px-4 sm:px-6"
        >
          <div className="relative mx-auto flex w-full max-w-xl items-center gap-3 rounded-full border border-zinc-200/50 bg-white/75 px-5 py-3 shadow-lg backdrop-blur-xl dark:border-zinc-800/60 dark:bg-zinc-900/70 sm:gap-5 sm:px-6">
            {/* Logo */}
            <TurboWordmark className="h-4 w-auto text-zinc-900 dark:text-zinc-100" />
            
            {/* Separator */}
            <div className="hidden h-6 w-px bg-zinc-300 dark:bg-zinc-700 sm:block" />
            
            {/* Quick Links */}
            <nav className="hidden items-center gap-1 md:flex">
              {NAV_ITEMS.slice(0, 3).map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item.href, item.overlay)}
                  className="rounded-full px-3 py-1.5 text-sm font-medium text-zinc-600 transition-all hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                >
                  {item.label}
                </button>
              ))}
            </nav>
            
            {/* Separator */}
            <div className="hidden h-6 w-px bg-zinc-300 dark:bg-zinc-700 md:block" />
            
            {/* CTA Buttons */}
            <div className="ml-auto flex items-center gap-2">
              {/* Contact Icon */}
              <button
                onClick={() => handleNavigation('/contact', '/contact')}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900 text-zinc-100 transition-colors hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
                aria-label="Contact"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 4h16v16H4z" strokeLinejoin="round" />
                  <path d="m4 7 8 5 8-5" strokeLinejoin="round" />
                </svg>
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="hidden h-9 w-9 items-center justify-center rounded-full bg-zinc-100 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 md:flex"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <svg className="h-4 w-4 text-zinc-600 dark:text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4 text-zinc-600 dark:text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
              
              {/* Menu Button */}
              <button
                onClick={handleMenuClick}
                className="flex items-center gap-2 rounded-full bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700 md:h-9 md:w-9 md:justify-center md:px-0 md:py-0"
                aria-label="Open menu"
              >
                <span className="md:hidden">{currentLabel}</span>
                {mobileMenuOpen ? (
                  <svg className="h-4 w-4 text-zinc-600 dark:text-zinc-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 6l12 12" />
                    <path d="M6 18L18 6" />
                  </svg>
                ) : (
                  <svg className="h-4 w-4 text-zinc-600 dark:text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>

            <AnimatePresence>
              {mobileMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 right-0 top-[calc(100%+12px)] z-50 rounded-3xl border border-zinc-200/60 bg-white/95 p-4 shadow-2xl backdrop-blur-xl dark:border-zinc-700/60 dark:bg-zinc-900/95"
                >
                  <div className="flex flex-col gap-3">
                    {NAV_ITEMS.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => handleNavigation(item.href, item.overlay)}
                        className="flex items-center justify-between rounded-2xl bg-zinc-100/70 px-4 py-3 text-left text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200 dark:bg-zinc-800/60 dark:text-zinc-100 dark:hover:bg-zinc-700"
                      >
                        <span>{item.label}</span>
                        <svg className="h-4 w-4 text-zinc-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M7 17L17 7" />
                          <path d="M7 7h10v10" />
                        </svg>
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center justify-between rounded-2xl bg-zinc-100/60 px-4 py-3 text-sm text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200">
                    <span>Theme</span>
                    <button
                      onClick={toggleTheme}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-zinc-700 transition-colors hover:bg-zinc-200 dark:bg-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-600"
                      aria-label="Toggle theme"
                    >
                      {isDark ? (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingNav;
